import React, { useEffect, useRef, useState } from "react";

/**
 * Simple single-image uploader with preview and validation
 * Props:
 * - onFileChange(file|null)
 * - initialPreview (string)
 * - maxSize (bytes) - default 10MB
 * - accept - default ['image/jpeg','image/png','image/jpg']
 */
const DEFAULT_MAX = 10 * 1024 * 1024;
const DEFAULT_ACCEPT = ["image/jpeg", "image/png", "image/jpg"];

export default function FileUpload({
  onFileChange,
  initialPreview = null,
  maxSize = DEFAULT_MAX,
  accept = DEFAULT_ACCEPT,
}) {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(initialPreview);
  const [error, setError] = useState("");

  useEffect(() => {
    return () => {
      if (preview && preview.startsWith("blob:")) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const open = () => inputRef.current?.click();

  const handle = (e) => {
    setError("");
    const file = e.target.files?.[0];
    if (!file) return;

    if (!accept.includes(file.type)) {
      setError("Allowed formats: JPG, JPEG, PNG");
      onFileChange(null);
      return;
    }

    if (file.size > maxSize) {
      setError(
        `Profile picture is too large. Please choose an image that is ${Math.round(maxSize / 1024 / 1024)} MB or smaller.`
      );
      onFileChange(null);
      return;
    }

    // cleanup previous preview
    if (preview && preview.startsWith("blob:")) URL.revokeObjectURL(preview);

    const url = URL.createObjectURL(file);
    setPreview(url);
    onFileChange(file);
    // reset input so same file can be chosen again if removed
    e.target.value = "";
  };

  return (
    <div className="rounded-lg border border-gray-200 p-6 flex flex-col items-center">
      {error && (
        <p className="text-red-600 text-sm mb-3 text-center" role="alert">
          {error}
        </p>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept.join(",")}
        className="hidden"
        onChange={handle}
      />

      <button
        type="button"
        onClick={open}
        className="w-36 h-36 rounded-full border-2 border-dashed border-gray-200 bg-gray-50 flex items-center justify-center hover:bg-gray-100"
        aria-label="Upload photo"
      >
        {preview ? (
          <img
            src={preview}
            alt="Preview"
            className="w-full h-full object-cover rounded-full"
          />
        ) : (
          <div className="flex flex-col items-center text-gray-500">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 mb-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M3 15a4 4 0 004 4h10a4 4 0 004-4V7a4 4 0 00-4-4H7a4 4 0 00-4 4v8z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M8 11l2 2 4-4"
              />
            </svg>
            <span className="text-sm">Upload photo</span>
          </div>
        )}
      </button>

      <div className="mt-6 text-center text-sm text-gray-600">
        <div>Allowed format</div>
        <div className="font-medium">JPG, JPEG, PNG</div>
        <div className="mt-3">Max file size</div>
        <div className="font-medium">{Math.round(maxSize / 1024 / 1024)}MB</div>
      </div>
    </div>
  );
}
